"use client";

import type { TouchEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { GalleryImageData } from "../../blocks/image-gallery/image-gallery.types";

const SWIPE_THRESHOLD = 50;

export type UseOverlayGalleryOptions = {
  images: GalleryImageData[];
  activeIndex: number;
  onImageChange: (index: number) => void;
  onClose?: () => void;
  initialZoomed?: boolean;
};

export function useOverlayGallery({
  images,
  activeIndex,
  onImageChange,
  onClose,
  initialZoomed = false,
}: UseOverlayGalleryOptions) {
  const [isZoomed, setIsZoomed] = useState(initialZoomed);
  const touchStartX = useRef<number | null>(null);

  const currentImage = images[activeIndex];
  const hasPrevious = activeIndex > 0;
  const hasNext = activeIndex < images.length - 1;
  const isVideo = Boolean(
    currentImage.mediaType === "video" && currentImage.videoUrl
  );

  // Preload adjacent images (previous/next) when the overlay/gallery is active.
  // This improves perceived responsiveness when the browser cache is empty.
  useEffect(() => {
    if (!images || images.length === 0) {
      return;
    }

    const preloadSrc = (src?: string | undefined) => {
      if (!src) {
        return;
      }
      try {
        const img = new Image();
        img.src = src;
      } catch (_e) {
        // ignore preload errors
      }
    };

    const next = images[activeIndex + 1]?.src;
    const prev = images[activeIndex - 1]?.src;

    preloadSrc(next);
    preloadSrc(prev);
  }, [images, activeIndex]);

  useEffect(() => {
    const handlePopState = () => {
      document.body.style.pointerEvents = "auto";
      document.body.style.overflow = "auto";
      onClose?.();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      document.body.style.pointerEvents = "auto";
      document.body.style.overflow = "auto";
    };
  }, [onClose]);

  const handleCloseZoom = useCallback(() => {
    setIsZoomed(false);
  }, []);

  const handleToggleZoom = useCallback(() => {
    setIsZoomed((prev) => !prev);
  }, []);

  const handlePrevious = useCallback(() => {
    if (!hasPrevious) {
      return;
    }
    handleCloseZoom();
    onImageChange(activeIndex - 1);
  }, [hasPrevious, activeIndex, onImageChange, handleCloseZoom]);

  const handleNext = useCallback(() => {
    if (!hasNext) {
      return;
    }
    handleCloseZoom();
    onImageChange(activeIndex + 1);
  }, [hasNext, activeIndex, onImageChange, handleCloseZoom]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && hasPrevious) {
        handlePrevious();
      }

      if (event.key === "ArrowRight" && hasNext) {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [hasPrevious, hasNext, handlePrevious, handleNext]);

  const handleTouchStart = useCallback((e: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      if (touchStartX.current === null) {
        return;
      }

      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX.current - touchEndX;

      if (Math.abs(diff) > SWIPE_THRESHOLD) {
        if (diff > 0 && hasNext) {
          handleNext();
        } else if (diff < 0 && hasPrevious) {
          handlePrevious();
        }
      }

      touchStartX.current = null;
    },
    [hasNext, hasPrevious, handleNext, handlePrevious]
  );

  const handleThumbnailClick = useCallback(
    (index: number) => {
      onImageChange(index);
      handleCloseZoom();
    },
    [onImageChange, handleCloseZoom]
  );

  return {
    isZoomed,
    currentImage,
    hasPrevious,
    hasNext,
    isVideo,
    handleCloseZoom,
    handleToggleZoom,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    handleTouchStart,
    handleTouchEnd,
  };
}
